/**
 * ORACLE Drive Storage  -  v2
 *
 * Structure per user:
 *   oracle-data.json          ← metadata only (fast load, always tiny)
 *   oracle-images/            ← folder of actual image files
 *     img_{id}.jpg
 *     img_{id}.jpg
 *
 * Images saved from web  → stored as URL only (no download, no Drive file)
 * Images from desktop    → compressed → uploaded as Drive file → Drive URL used
 */

const DRIVE_FILES_URL  = "https://www.googleapis.com/drive/v3/files";
const DRIVE_UPLOAD_URL = "https://www.googleapis.com/upload/drive/v3/files";
const META_FILE_NAME   = "oracle-data.json";
const IMG_FOLDER_NAME  = "oracle-images";

// ── localStorage keys ──────────────────────────────────────────────────────
const LS = {
  get:     k => { try { return localStorage.getItem(k); } catch { return null; } },
  set:     (k, v) => { try { localStorage.setItem(k, v); } catch {} },
  del:     k => { try { localStorage.removeItem(k); } catch {} },
  getJSON: k => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch { return null; } },
  setJSON: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
};

// ── Auth header ────────────────────────────────────────────────────────────
const auth = token => ({ Authorization: `Bearer ${token}` });

// ── Find or create a Drive file/folder by name ────────────────────────────
async function findFile(token, name, mimeType, parentId = null) {
  let q = `name='${name}' and trashed=false`;
  if (mimeType) q += ` and mimeType='${mimeType}'`;
  if (parentId) q += ` and '${parentId}' in parents`;
  const res = await fetch(
    `${DRIVE_FILES_URL}?q=${encodeURIComponent(q)}&fields=files(id,name)&spaces=drive`,
    { headers: auth(token) }
  );
  if (!res.ok) return null;
  const data = await res.json();
  return data.files?.[0]?.id || null;
}

async function createFolder(token, name, parentId = null) {
  const meta = { name, mimeType: "application/vnd.google-apps.folder" };
  if (parentId) meta.parents = [parentId];
  const res = await fetch(`${DRIVE_FILES_URL}?fields=id`, {
    method: "POST",
    headers: { ...auth(token), "Content-Type": "application/json" },
    body: JSON.stringify(meta),
  });
  if (!res.ok) throw new Error("Failed to create Drive folder");
  const data = await res.json();
  return data.id;
}

async function createEmptyFile(token, name, mimeType, parentId = null, content = "") {
  const meta = { name, mimeType };
  if (parentId) meta.parents = [parentId];
  const form = new FormData();
  form.append("metadata", new Blob([JSON.stringify(meta)], { type: "application/json" }));
  form.append("file",     new Blob([content],              { type: mimeType }));
  const res = await fetch(`${DRIVE_UPLOAD_URL}?uploadType=multipart&fields=id`, {
    method: "POST", headers: auth(token), body: form,
  });
  if (!res.ok) throw new Error("Failed to create Drive file");
  return (await res.json()).id;
}

// ── Resolve oracle folder structure ───────────────────────────────────────
async function resolveStructure(token) {
  const cacheKey = "oracle_drive_structure";
  const cached = LS.getJSON(cacheKey);

  // Verify cached IDs still exist
  if (cached?.metaId && cached?.folderId) {
    const check = await fetch(`${DRIVE_FILES_URL}/${cached.metaId}?fields=id`, { headers: auth(token) });
    if (check.ok) return cached;
    LS.del(cacheKey);
  }

  // Find or create oracle-images folder
  let folderId = await findFile(token, IMG_FOLDER_NAME, "application/vnd.google-apps.folder");
  if (!folderId) folderId = await createFolder(token, IMG_FOLDER_NAME);

  // Find or create oracle-data.json
  let metaId = await findFile(token, META_FILE_NAME, "application/json");
  if (!metaId) {
    const empty = JSON.stringify({ images:[], palettes:[], moodboards:[], version:2 });
    metaId = await createEmptyFile(token, META_FILE_NAME, "application/json", null, empty);
  }

  const structure = { metaId, folderId };
  LS.setJSON(cacheKey, structure);
  return structure;
}

// ── Read metadata JSON ─────────────────────────────────────────────────────
export async function readFromDrive(token) {
  try {
    const { metaId } = await resolveStructure(token);
    const res = await fetch(`${DRIVE_FILES_URL}/${metaId}?alt=media`, { headers: auth(token) });
    if (!res.ok) return null;
    const data = await res.json();

    if (data.images) {
      // Migration: fix broken Drive-hosted URLs from previous storage attempt
      // lh3.googleusercontent.com and drive.google.com URLs need auth  -  convert to base64
      const brokenDriveUrl = url =>
        url && (url.includes("lh3.googleusercontent.com") || url.includes("drive.google.com/uc"));

      const needsMigration = data.images.some(img => brokenDriveUrl(img.url));

      if (needsMigration) {
        console.log("ORACLE: migrating Drive-hosted image URLs to base64...");
        const migrated = await Promise.all(data.images.map(async img => {
          if (!brokenDriveUrl(img.url)) return img;
          try {
            // Fetch the image using token (Drive file API with alt=media)
            const fileId = img.driveFileId;
            if (!fileId) return null; // can't recover without file ID
            const r = await fetch(`${DRIVE_FILES_URL}/${fileId}?alt=media`, { headers: auth(token) });
            if (!r.ok) return null;
            const blob = await r.blob();
            const base64 = await new Promise(resolve => {
              const reader = new FileReader();
              reader.onload = e => resolve(e.target.result);
              reader.readAsDataURL(blob);
            });
            return { ...img, url: base64, driveUrl: null };
          } catch { return null; }
        }));

        data.images = migrated.filter(Boolean);
        console.log(`ORACLE: migrated ${data.images.length} images to base64`);
      }

      // Also strip any remaining broken base64-less images
      data.images = data.images.filter(img => img.url && !img.url.includes("lh3.googleusercontent.com"));
    }

    return data;
  } catch(e) {
    console.error("Drive read failed:", e);
    return null;
  }
}

// ── Write metadata JSON ────────────────────────────────────────────────────
export async function writeToDrive(token, data) {
  try {
    const { metaId } = await resolveStructure(token);

    const res = await fetch(`${DRIVE_UPLOAD_URL}/${metaId}?uploadType=media`, {
      method: "PATCH",
      headers: { ...auth(token), "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    if (!res.ok) {
      console.error("Drive write failed:", res.status, await res.text());
      return false;
    }
    return true;
  } catch(e) {
    console.error("Drive write error:", e);
    return false;
  }
}

// ── Upload an image file to oracle-images/ folder ─────────────────────────
/**
 * Upload a base64 or blob image to Drive.
 * Returns { driveFileId, driveUrl } or null on failure.
 */
export async function uploadImageToDrive(token, imageData, filename) {
  try {
    const { folderId } = await resolveStructure(token);

    // Convert base64 to blob if needed
    let blob;
    if (typeof imageData === "string" && imageData.startsWith("data:")) {
      const [header, b64] = imageData.split(",");
      const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
      const bytes = atob(b64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      blob = new Blob([arr], { type: mime });
    } else if (imageData instanceof Blob) {
      blob = imageData;
    } else {
      return null;
    }

    const meta = { name: filename, parents: [folderId] };
    const form = new FormData();
    form.append("metadata", new Blob([JSON.stringify(meta)], { type: "application/json" }));
    form.append("file", blob);

    const res = await fetch(
      `${DRIVE_UPLOAD_URL}?uploadType=multipart&fields=id,webContentLink,webViewLink`,
      { method: "POST", headers: auth(token), body: form }
    );

    if (!res.ok) {
      console.error("Image upload failed:", res.status);
      return null;
    }

    const file = await res.json();

    // Make file publicly readable so ORACLE can display it via URL
    await fetch(`${DRIVE_FILES_URL}/${file.id}/permissions`, {
      method: "POST",
      headers: { ...auth(token), "Content-Type": "application/json" },
      body: JSON.stringify({ role: "reader", type: "anyone" }),
    });

    // drive.google.com/thumbnail works for public files without auth token in <img> src
    const driveUrl = `https://drive.google.com/thumbnail?id=${file.id}&sz=w1200`;
    return { driveFileId: file.id, driveUrl };
  } catch(e) {
    console.error("Image upload error:", e);
    return null;
  }
}

// ── Delete an image file from Drive ───────────────────────────────────────
export async function deleteImageFromDrive(token, driveFileId) {
  if (!driveFileId) return;
  try {
    await fetch(`${DRIVE_FILES_URL}/${driveFileId}`, {
      method: "DELETE", headers: auth(token),
    });
  } catch {}
}

// Fallback: find and delete a file in oracle-images/ by filename (when driveFileId not stored)
export async function deleteImageFromDriveByName(token, filename) {
  if (!filename) return;
  try {
    const { folderId } = await resolveStructure(token);
    if (!folderId) return;
    const q = encodeURIComponent(`name='${filename}' and '${folderId}' in parents and trashed=false`);
    const res = await fetch(`${DRIVE_FILES_URL}?q=${q}&fields=files(id)&spaces=drive`, { headers: auth(token) });
    if (!res.ok) return;
    const data = await res.json();
    for (const file of data.files || []) {
      await fetch(`${DRIVE_FILES_URL}/${file.id}`, { method: "DELETE", headers: auth(token) });
    }
  } catch {}
}

// ── User info ──────────────────────────────────────────────────────────────
export async function fetchUserInfo(token) {
  try {
    const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", { headers: auth(token) });
    return res.ok ? res.json() : null;
  } catch { return null; }
}

// ── Last sync time helper ──────────────────────────────────────────────────
export function getLastSyncTime() {
  return LS.get("oracle_last_sync") || null;
}
export function setLastSyncTime() {
  LS.set("oracle_last_sync", new Date().toISOString());
}
